import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  ArrowLeft, ArrowRight, Users, FileText, BarChart3,
  Download, ChevronDown, ChevronUp, Clock, Target
} from 'lucide-react';
import { getWeeklySummary, getCenterPlan, getRelatedDepartments, getCarryOverStats, downloadBlob, exportKpiExcel } from '../services/api';

// ─── Вспомогательные функции ──────────────────────────────────────────────

/** Возвращает понедельник текущей недели */
function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Форматирует дату в YYYY-MM-DD */
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Форматирует дату для отображения DD.MM.YYYY */
function formatDisplay(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

// ─── Константы вкладок ────────────────────────────────────────────────────

const TAB_ICONS = {
  overview: BarChart3,
  center_plan: FileText,
  related: Users,
  carry_over: AlertTriangle,
};

const TAB_KEYS = ['overview', 'center_plan', 'related', 'carry_over'];
const TAB_I18N_KEYS = {
  overview: 'overview',
  center_plan: 'centerPlan',
  related: 'relatedDepts',
  carry_over: 'carryOver',
};

// ─── Стили ────────────────────────────────────────────────────────────────

const styles = {
  page: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '24px',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  // Выбор недели
  weekSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  weekLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginRight: '4px',
  },
  dateInput: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1e293b',
    outline: 'none',
    backgroundColor: '#fff',
  },
  dateInputFocused: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 2px rgba(59,130,246,0.15)',
  },
  dateSeparator: {
    color: '#94a3b8',
    fontSize: '14px',
  },
  presetBtn: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#475569',
    transition: 'all 0.15s',
    fontWeight: '500',
  },
  presetBtnActive: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    borderColor: '#3b82f6',
  },
  navBtn: {
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  refreshBtn: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: '#3b82f6',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '500',
    marginLeft: 'auto',
    transition: 'all 0.15s',
  },
  // Вкладки
  tabsContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    backgroundColor: '#f1f5f9',
    borderRadius: '12px',
    padding: '4px',
  },
  tab: {
    flex: 1,
    padding: '10px 16px',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.15s',
  },
  tabActive: {
    backgroundColor: '#fff',
    color: '#1e293b',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  // Карточки статистики
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  statIconWrap: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px',
  },
  // Таблица
  tableCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  tableHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#1e293b',
    borderBottom: '1px solid #f1f5f9',
  },
  // Строки таблицы с цветами
  rowGreen: {
    backgroundColor: '#f0fdf4',
  },
  rowYellow: {
    backgroundColor: '#fefce8',
  },
  rowRed: {
    backgroundColor: '#fef2f2',
  },
  // Значки
  checkIcon: {
    color: '#16a34a',
  },
  xIcon: {
    color: '#dc2626',
  },
  // Прогресс-бар
  progressBarOuter: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s',
  },
  // Секция сводного плана
  deptSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    marginBottom: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  deptSectionHeader: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    transition: 'background-color 0.15s',
  },
  deptSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  deptSectionBody: {
    padding: '0',
  },
  taskRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid #f1f5f9',
    gap: '12px',
  },
  taskTitle: {
    flex: 1,
    fontSize: '14px',
    color: '#1e293b',
  },
  taskMeta: {
    fontSize: '12px',
    color: '#64748b',
  },
  // Карточки связанных отделов
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  relatedCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  relatedCardTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  relatedDeptTag: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    margin: '3px 4px 3px 0',
  },
  relatedReason: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '8px',
    fontStyle: 'italic',
  },
  // Бейджи
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  badgeDanger: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  badgeSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  // Загрузка
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#64748b',
    fontSize: '15px',
    gap: '10px',
  },
  // Пустой блок
  emptyState: {
    textAlign: 'center',
    padding: '48px 20px',
    color: '#94a3b8',
    fontSize: '14px',
  },
  emptyIcon: {
    marginBottom: '12px',
    color: '#cbd5e1',
  },
  // Статус задачи
  taskStatusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  // Сводка переносов
  carryStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  carryStatCard: {
    padding: '16px',
    borderRadius: '10px',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  // Экспорт кнопки
  exportBtnGroup: {
    display: 'flex',
    gap: '8px',
  },
  exportBtn: {
    padding: '8px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.15s',
  },
};

// ─── Компонент ────────────────────────────────────────────────────────────

function WeeklyControl() {
  const { t } = useTranslation(['weeklyControl', 'common']);

  const TABS = TAB_KEYS.map(key => ({
    key,
    label: t(`weeklyControl:tabs.${TAB_I18N_KEYS[key]}`),
    icon: TAB_ICONS[key],
  }));

  const now = new Date();
  const mondayNow = getMonday(now);
  const sundayNow = new Date(mondayNow);
  sundayNow.setDate(sundayNow.getDate() + 6);

  // Даты недели
  const [startDate, setStartDate] = useState(formatDate(mondayNow));
  const [endDate, setEndDate] = useState(formatDate(sundayNow));
  const [activePreset, setActivePreset] = useState('current');

  // Вкладки
  const [activeTab, setActiveTab] = useState('overview');

  // Данные
  const [summaryData, setSummaryData] = useState(null);
  const [centerPlanData, setCenterPlanData] = useState(null);
  const [relatedData, setRelatedData] = useState(null);
  const [carryOverData, setCarryOverData] = useState(null);

  // Состояние
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDepts, setExpandedDepts] = useState({});

  // ─── Загрузка данных по вкладке ────────────────────────────────────

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);
    try {
      switch (tab) {
        case 'overview': {
          const res = await getWeeklySummary({ start_date: startDate, end_date: endDate });
          setSummaryData(res.data);
          break;
        }
        case 'center_plan': {
          const res = await getCenterPlan({ start_date: startDate, end_date: endDate });
          setCenterPlanData(res.data);
          break;
        }
        case 'related': {
          const res = await getRelatedDepartments();
          setRelatedData(res.data);
          break;
        }
        case 'carry_over': {
          const res = await getCarryOverStats({ start_date: startDate, end_date: endDate });
          setCarryOverData(res.data);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error(`Ошибка загрузки вкладки "${tab}":`, err);
      setError(t('weeklyControl:errors.loadFailed'));
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  // ─── Навигация по неделям ──────────────────────────────────────────

  const setWeek = useCallback((mondayDate, preset) => {
    const monday = new Date(mondayDate);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    setStartDate(formatDate(monday));
    setEndDate(formatDate(sunday));
    setActivePreset(preset);
  }, []);

  const goCurrentWeek = useCallback(() => {
    setWeek(getMonday(new Date()), 'current');
  }, [setWeek]);

  const goPreviousWeek = useCallback(() => {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() - 7);
    setWeek(monday, 'previous');
  }, [startDate, setWeek]);

  const goNextWeek = useCallback(() => {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() + 7);
    setWeek(monday, null);
  }, [startDate, setWeek]);

  const goPrevWeekPreset = useCallback(() => {
    const monday = getMonday(new Date());
    monday.setDate(monday.getDate() - 7);
    setWeek(monday, 'previous');
  }, [setWeek]);

  const handleRefresh = useCallback(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleExport = useCallback(async () => {
    try {
      const res = await exportKpiExcel({ year: new Date(startDate).getFullYear() });
      downloadBlob(res, `${t('weeklyControl:exportFilename')}_${startDate}_${endDate}.xlsx`);
    } catch (err) {
      console.error('Ошибка экспорта:', err);
      alert(t('weeklyControl:errors.exportFailed'));
    }
  }, [startDate, endDate]);

  // Раскрытие/скрытие отделов в сводном плане
  const toggleDept = useCallback((deptId) => {
    setExpandedDepts(prev => ({ ...prev, [deptId]: !prev[deptId] }));
  }, []);

  // ─── Цвет строки таблицы обзора ───────────────────────────────────

  const getRowStyle = (dept) => {
    const hasPlan = dept.has_plan;
    const hasReport = dept.has_report;
    if (hasPlan && hasReport) return styles.rowGreen;
    if (!hasPlan && !hasReport) return styles.rowRed;
    return styles.rowYellow;
  };

  // ─── Цвет прогресс-бара ───────────────────────────────────────────

  const getProgressColor = (pct) => {
    if (pct >= 80) return '#16a34a';
    if (pct >= 50) return '#eab308';
    return '#dc2626';
  };

  // ─── Цвет бейджа статуса задачи ───────────────────────────────────

  const getTaskStatusStyle = (status) => {
    switch (status) {
      case 'completed':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      case 'in_progress':
        return { backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'not_started':
        return { backgroundColor: '#f1f5f9', color: '#475569' };
      case 'overdue':
        return { backgroundColor: '#fee2e2', color: '#991b1b' };
      default:
        return { backgroundColor: '#f1f5f9', color: '#475569' };
    }
  };

  const getTaskStatusLabel = (status) => {
    const key = `weeklyControl:taskStatus.${status}`;
    const label = t(key);
    return label !== key ? label : (status || t('weeklyControl:taskStatus.default'));
  };

  // ─── Вкладка «Обзор» ──────────────────────────────────────────────

  const renderOverview = () => {
    if (!summaryData) return renderEmpty(t('weeklyControl:noDataMessage'));

    const departments = summaryData.departments || [];
    const totalDepts = departments.length || 30;
    const plansSubmitted = departments.filter(d => d.has_plan).length;
    const reportsSubmitted = departments.filter(d => d.has_report).length;
    const reportsOnTime = departments.filter(d => d.report_on_time === true).length;
    const reportsLate = departments.filter(d => d.report_on_time === false).length;
    const totalTasks = departments.reduce((sum, d) => sum + (d.tasks_count || 0), 0);
    const avgCompletion = departments.length > 0
      ? Math.round(departments.reduce((sum, d) => sum + (d.completion_percentage || 0), 0) / departments.length)
      : 0;
    const totalCarried = departments.reduce((sum, d) => sum + (d.carried_tasks_count || 0), 0);

    return (
      <div>
        {/* Статистические карточки */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconWrap, backgroundColor: '#eff6ff' }}>
              <Users size={24} color="#3b82f6" />
            </div>
            <div>
              <div style={styles.statValue}>{totalDepts}</div>
              <div style={styles.statLabel}>{t('weeklyControl:overview.totalDepartments')}</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconWrap, backgroundColor: '#f0fdf4' }}>
              <FileText size={24} color="#16a34a" />
            </div>
            <div>
              <div style={styles.statValue}>
                {plansSubmitted}<span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '400' }}>/{totalDepts}</span>
              </div>
              <div style={styles.statLabel}>{t('weeklyControl:overview.plansSubmitted')}</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconWrap, backgroundColor: '#fef3c7' }}>
              <BarChart3 size={24} color="#d97706" />
            </div>
            <div>
              <div style={styles.statValue}>
                {reportsSubmitted}<span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '400' }}>/{totalDepts}</span>
              </div>
              <div style={styles.statLabel}>{t('weeklyControl:overview.reportsSubmitted')}</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconWrap, backgroundColor: reportsLate > 0 ? '#fef2f2' : '#f0fdf4' }}>
              <Clock size={24} color={reportsLate > 0 ? '#dc2626' : '#16a34a'} />
            </div>
            <div>
              <div style={styles.statValue}>
                {reportsOnTime}<span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '400' }}>/{reportsSubmitted}</span>
              </div>
              <div style={styles.statLabel}>{t('weeklyControl:overview.onTime')}{reportsLate > 0 ? ` (${reportsLate} ${t('weeklyControl:overview.late')})` : ''}</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconWrap, backgroundColor: '#faf5ff' }}>
              <Target size={24} color="#7c3aed" />
            </div>
            <div>
              <div style={styles.statValue}>{avgCompletion}%</div>
              <div style={styles.statLabel}>{t('weeklyControl:overview.avgCompletion')}</div>
            </div>
          </div>
        </div>

        {/* Таблица отделов */}
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              <BarChart3 size={18} />
              {t('weeklyControl:overview.summaryFor', { start: formatDisplay(startDate), end: formatDisplay(endDate) })}
            </h3>
            <div style={styles.exportBtnGroup}>
              <button style={styles.exportBtn} onClick={handleExport}>
                <Download size={14} /> Excel
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t('common:table.department')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:overview.plan')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:overview.report')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:overview.timeliness')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:overview.tasks')}</th>
                  <th style={{ ...styles.th, width: '180px' }}>{t('weeklyControl:overview.completion')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:overview.carryOver')}</th>
                </tr>
              </thead>
              <tbody>
                {departments.length === 0 ? (
                  <tr>
                    <td style={{ ...styles.td, textAlign: 'center' }} colSpan={7}>
                      {t('weeklyControl:overview.noDataForPeriod')}
                    </td>
                  </tr>
                ) : (
                  departments.map((dept, idx) => {
                    const pct = dept.completion_percentage || 0;
                    return (
                      <tr key={dept.department_id || idx} style={getRowStyle(dept)}>
                        <td style={{ ...styles.td, fontWeight: '500' }}>
                          {dept.department_name || t('weeklyControl:departmentFallback', { id: dept.department_id })}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {dept.has_plan ? (
                            <CheckCircle size={18} style={styles.checkIcon} />
                          ) : (
                            <XCircle size={18} style={styles.xIcon} />
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {dept.has_report ? (
                            <CheckCircle size={18} style={styles.checkIcon} />
                          ) : (
                            <XCircle size={18} style={styles.xIcon} />
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {dept.report_on_time === true ? (
                            <span style={{ ...styles.badge, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                              {t('common:onTime')}
                            </span>
                          ) : dept.report_on_time === false ? (
                            <span style={{ ...styles.badge, backgroundColor: '#fef2f2', color: '#dc2626' }}>
                              +{dept.report_delay_hours || 0}{t('weeklyControl:hoursShort')}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {dept.tasks_count || 0}
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={styles.progressBarOuter}>
                              <div
                                style={{
                                  ...styles.progressBarInner,
                                  width: `${Math.min(pct, 100)}%`,
                                  backgroundColor: getProgressColor(pct),
                                }}
                              />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: getProgressColor(pct), minWidth: '38px' }}>
                              {pct}%
                            </span>
                          </div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {(dept.carried_tasks_count || 0) > 0 ? (
                            <span style={{ ...styles.badge, ...styles.badgeWarning }}>
                              {dept.carried_tasks_count}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Итого */}
          {departments.length > 0 && (
            <div style={{
              padding: '12px 20px',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              gap: '24px',
              fontSize: '13px',
              color: '#64748b',
              backgroundColor: '#f8fafc',
            }}>
              <span>{t('weeklyControl:overview.totalTasks')}: <strong style={{ color: '#1e293b' }}>{totalTasks}</strong></span>
              <span>{t('weeklyControl:overview.carried')}: <strong style={{ color: '#d97706' }}>{totalCarried}</strong></span>
              <span>{t('weeklyControl:overview.plans')}: <strong style={{ color: plansSubmitted === totalDepts ? '#16a34a' : '#dc2626' }}>{plansSubmitted}/{totalDepts}</strong></span>
              <span>{t('weeklyControl:overview.reports')}: <strong style={{ color: reportsSubmitted === totalDepts ? '#16a34a' : '#dc2626' }}>{reportsSubmitted}/{totalDepts}</strong></span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Вкладка «Сводный план» ───────────────────────────────────────

  const renderCenterPlan = () => {
    if (!centerPlanData) return renderEmpty(t('weeklyControl:centerPlan.noPlans'));

    const departments = centerPlanData.departments || centerPlanData || [];
    const deptsList = Array.isArray(departments) ? departments : [];

    if (deptsList.length === 0) {
      return renderEmpty(t('weeklyControl:centerPlan.noPlans'));
    }

    return (
      <div>
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>
          {t('weeklyControl:centerPlan.allTasksFor', { start: formatDisplay(startDate), end: formatDisplay(endDate) })}
        </div>
        {deptsList.map((dept, idx) => {
          const deptId = dept.department_id || idx;
          const isExpanded = expandedDepts[deptId] !== false; // по умолчанию раскрыто
          const tasks = dept.tasks || [];

          return (
            <div key={deptId} style={styles.deptSection}>
              <div
                style={styles.deptSectionHeader}
                onClick={() => toggleDept(deptId)}
              >
                <h4 style={styles.deptSectionTitle}>
                  {dept.department_name || t('weeklyControl:departmentFallback', { id: deptId })}
                  <span style={{ fontWeight: '400', color: '#64748b', marginLeft: '8px', fontSize: '13px' }}>
                    ({tasks.length} {tasks.length === 1 ? t('weeklyControl:centerPlan.taskWord_one') : tasks.length >= 2 && tasks.length <= 4 ? t('weeklyControl:centerPlan.taskWord_few') : t('weeklyControl:centerPlan.taskWord_many')})
                  </span>
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {dept.plan_title && (
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      {dept.plan_title}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
                </div>
              </div>
              {isExpanded && (
                <div style={styles.deptSectionBody}>
                  {tasks.length === 0 ? (
                    <div style={{ padding: '16px 20px', color: '#94a3b8', fontSize: '14px' }}>
                      {t('weeklyControl:centerPlan.noTasks')}
                    </div>
                  ) : (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>{t('weeklyControl:centerPlan.task')}</th>
                          <th style={{ ...styles.th, width: '120px' }}>{t('weeklyControl:centerPlan.status')}</th>
                          <th style={{ ...styles.th, width: '110px' }}>{t('weeklyControl:centerPlan.deadline')}</th>
                          <th style={{ ...styles.th, width: '100px' }}>{t('weeklyControl:centerPlan.completion')}</th>
                          <th style={{ ...styles.th, width: '100px', textAlign: 'center' }}>{t('weeklyControl:centerPlan.carried')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, tIdx) => (
                          <tr key={task.id || tIdx}>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {task.is_carried_over && (
                                  <span style={{ ...styles.badge, ...styles.badgeWarning, fontSize: '10px' }} title={t('weeklyControl:centerPlan.carriedBadge')}>
                                    {t('weeklyControl:centerPlan.carriedBadge')}
                                  </span>
                                )}
                                <span>{task.title || t('weeklyControl:centerPlan.noTitle')}</span>
                              </div>
                              {task.description && (
                                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                                  {task.description.length > 100 ? task.description.substring(0, 100) + '...' : task.description}
                                </div>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={{ ...styles.taskStatusBadge, ...getTaskStatusStyle(task.status) }}>
                                {getTaskStatusLabel(task.status)}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                                <Clock size={13} color="#94a3b8" />
                                {task.deadline ? formatDisplay(task.deadline) : '—'}
                              </div>
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ ...styles.progressBarOuter, width: '60px' }}>
                                  <div
                                    style={{
                                      ...styles.progressBarInner,
                                      width: `${Math.min(task.completion_percentage || 0, 100)}%`,
                                      backgroundColor: getProgressColor(task.completion_percentage || 0),
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                                  {task.completion_percentage || 0}%
                                </span>
                              </div>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              {(task.carry_count || 0) > 0 ? (
                                <span style={{
                                  ...styles.badge,
                                  ...(task.carry_count > 2 ? styles.badgeDanger : styles.badgeWarning),
                                }}>
                                  {task.carry_count}x
                                </span>
                              ) : (
                                <span style={{ color: '#cbd5e1' }}>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Вкладка «Связанные отделы» ───────────────────────────────────

  const renderRelated = () => {
    if (!relatedData) return renderEmpty(t('weeklyControl:related.noData'));

    const groups = relatedData.groups || relatedData || [];
    const groupsList = Array.isArray(groups) ? groups : [];

    if (groupsList.length === 0) {
      return renderEmpty(t('weeklyControl:related.noGroups'));
    }

    return (
      <div>
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>
          {t('weeklyControl:related.description')}
        </div>
        <div style={styles.relatedGrid}>
          {groupsList.map((group, idx) => {
            const departments = group.departments || [];
            return (
              <div key={idx} style={styles.relatedCard}>
                <div style={styles.relatedCardTitle}>
                  <Users size={18} color="#3b82f6" />
                  {group.name || t('weeklyControl:groupFallback', { num: idx + 1 })}
                </div>
                <div>
                  {departments.map((dept, dIdx) => (
                    <span key={dIdx} style={styles.relatedDeptTag}>
                      {dept.name || dept.department_name || dept}
                    </span>
                  ))}
                </div>
                {group.reason && (
                  <div style={styles.relatedReason}>
                    {t('weeklyControl:related.link')}: {group.reason}
                  </div>
                )}
                {group.common_categories && group.common_categories.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{t('weeklyControl:related.commonCategories')}: </span>
                    {group.common_categories.map((cat, cIdx) => (
                      <span key={cIdx} style={{ ...styles.relatedDeptTag, backgroundColor: '#faf5ff', color: '#7c3aed' }}>
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
                {group.shared_documents_count > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>
                    {t('weeklyControl:related.sharedDocuments')}: <strong>{group.shared_documents_count}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Вкладка «Проблемные переносы» ────────────────────────────────

  const renderCarryOver = () => {
    if (!carryOverData) return renderEmpty(t('weeklyControl:carryOver.noData'));

    const tasks = carryOverData.chronic_tasks || carryOverData.tasks || [];
    const byDepartment = carryOverData.by_department || [];
    const totalCarried = carryOverData.total_carried || tasks.length;
    const chronicCount = carryOverData.chronic_count || tasks.filter(t => (t.carry_count || 0) > 2).length;

    return (
      <div>
        {/* Сводные карточки */}
        <div style={styles.carryStatsGrid}>
          <div style={styles.carryStatCard}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#d97706' }}>{totalCarried}</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{t('weeklyControl:carryOver.totalCarryOvers')}</div>
          </div>
          <div style={styles.carryStatCard}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626' }}>{chronicCount}</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{t('weeklyControl:carryOver.chronic')}</div>
          </div>
          <div style={styles.carryStatCard}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>
              {byDepartment.length || new Set(tasks.map(t => t.department_name)).size}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{t('weeklyControl:carryOver.deptsWithCarryOvers')}</div>
          </div>
        </div>

        {/* Топ отделов по переносам */}
        {byDepartment.length > 0 && (
          <div style={{ ...styles.tableCard, marginBottom: '20px' }}>
            <div style={styles.tableHeader}>
              <h3 style={styles.tableTitle}>
                <AlertTriangle size={18} color="#d97706" />
                {t('weeklyControl:carryOver.byDepartments')}
              </h3>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t('weeklyControl:carryOver.department')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:carryOver.carryOvers')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:carryOver.chronicCount')}</th>
                </tr>
              </thead>
              <tbody>
                {byDepartment.map((dept, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{dept.department_name}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={{ ...styles.badge, ...styles.badgeWarning }}>{dept.carry_count || dept.total}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {(dept.chronic_count || 0) > 0 ? (
                        <span style={{ ...styles.badge, ...styles.badgeDanger }}>{dept.chronic_count}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Таблица хронических задач */}
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              <AlertTriangle size={18} color="#dc2626" />
              {t('weeklyControl:carryOver.chronicTasks')}
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t('weeklyControl:carryOver.task')}</th>
                  <th style={styles.th}>{t('weeklyControl:carryOver.department')}</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>{t('weeklyControl:carryOver.carryCount')}</th>
                  <th style={{ ...styles.th, width: '120px' }}>{t('weeklyControl:carryOver.status')}</th>
                  <th style={{ ...styles.th, width: '110px' }}>{t('weeklyControl:carryOver.deadline')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td style={{ ...styles.td, textAlign: 'center', color: '#94a3b8' }} colSpan={5}>
                      {t('weeklyControl:carryOver.noChronicTasks')}
                    </td>
                  </tr>
                ) : (
                  tasks.map((task, idx) => (
                    <tr key={task.id || idx}>
                      <td style={{ ...styles.td, fontWeight: '500' }}>
                        {task.title || t('weeklyControl:centerPlan.noTitle')}
                        {task.description && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px', fontWeight: '400' }}>
                            {task.description.length > 80 ? task.description.substring(0, 80) + '...' : task.description}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        {task.department_name || '—'}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <span style={{
                          ...styles.badge,
                          ...(task.carry_count > 3 ? styles.badgeDanger : styles.badgeWarning),
                          fontSize: '14px',
                          fontWeight: '700',
                        }}>
                          {task.carry_count || 0}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.taskStatusBadge, ...getTaskStatusStyle(task.status) }}>
                          {getTaskStatusLabel(task.status)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                          <Clock size={13} color="#94a3b8" />
                          {task.deadline ? formatDisplay(task.deadline) : '—'}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Пустое состояние ─────────────────────────────────────────────

  const renderEmpty = (message) => (
    <div style={styles.emptyState}>
      <div style={styles.emptyIcon}>
        <FileText size={48} />
      </div>
      <div>{message}</div>
    </div>
  );

  // ─── Спиннер загрузки ─────────────────────────────────────────────

  const renderLoading = () => (
    <div style={styles.loading}>
      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
      {t('weeklyControl:loading')}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ─── Ошибка ───────────────────────────────────────────────────────

  const renderError = () => (
    <div style={{
      padding: '24px',
      textAlign: 'center',
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      border: '1px solid #fecaca',
    }}>
      <AlertTriangle size={24} style={{ marginBottom: '8px' }} />
      <div>{error}</div>
      <button
        style={{ ...styles.refreshBtn, margin: '12px auto 0', backgroundColor: '#dc2626' }}
        onClick={handleRefresh}
      >
        <RefreshCw size={14} /> {t('weeklyControl:retry')}
      </button>
    </div>
  );

  // ─── Контент текущей вкладки ──────────────────────────────────────

  const renderTabContent = () => {
    if (loading) return renderLoading();
    if (error) return renderError();

    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'center_plan': return renderCenterPlan();
      case 'related': return renderRelated();
      case 'carry_over': return renderCarryOver();
      default: return null;
    }
  };

  // ─── Рендер страницы ──────────────────────────────────────────────

  return (
    <div style={styles.page}>
      {/* Заголовок */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>
          <Calendar size={28} color="#3b82f6" />
          {t('weeklyControl:title')}
        </h1>
        <p style={styles.headerSubtitle}>
          {t('weeklyControl:subtitle')}
        </p>
      </div>

      {/* Выбор недели */}
      <div style={styles.weekSelector}>
        <span style={styles.weekLabel}>{t('weeklyControl:week')}:</span>

        <button
          style={styles.navBtn}
          onClick={goNextWeek}
          title={t('weeklyControl:nextWeek')}
        >
          <ArrowLeft size={16} />
        </button>

        <input
          type="date"
          value={startDate}
          onChange={e => {
            setStartDate(e.target.value);
            setActivePreset(null);
          }}
          style={styles.dateInput}
        />
        <span style={styles.dateSeparator}>—</span>
        <input
          type="date"
          value={endDate}
          onChange={e => {
            setEndDate(e.target.value);
            setActivePreset(null);
          }}
          style={styles.dateInput}
        />

        <button
          style={styles.navBtn}
          onClick={goPreviousWeek}
          title={t('weeklyControl:previousWeek')}
        >
          <ArrowRight size={16} />
        </button>

        <div style={{ width: '1px', height: '24px', backgroundColor: '#e2e8f0', margin: '0 4px' }} />

        <button
          style={{
            ...styles.presetBtn,
            ...(activePreset === 'current' ? styles.presetBtnActive : {}),
          }}
          onClick={goCurrentWeek}
        >
          {t('weeklyControl:currentWeek')}
        </button>
        <button
          style={{
            ...styles.presetBtn,
            ...(activePreset === 'previous' ? styles.presetBtnActive : {}),
          }}
          onClick={goPrevWeekPreset}
        >
          {t('weeklyControl:previousWeek')}
        </button>

        <button
          style={styles.refreshBtn}
          onClick={handleRefresh}
          title={t('weeklyControl:refresh')}
        >
          <RefreshCw size={14} /> {t('weeklyControl:refresh')}
        </button>
      </div>

      {/* Вкладки */}
      <div style={styles.tabsContainer}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              style={{
                ...styles.tab,
                ...(isActive ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Контент вкладки */}
      {renderTabContent()}
    </div>
  );
}

export default WeeklyControl;
